import React from 'react';
import type { SalesOrderFormData } from '../../types/salesOrder';
import type { Customer } from '../../types/customer';
import { useSalesOrderForm } from './useSalesOrderForm';
import { SalesOrderHeaderSection } from './SalesOrderHeaderSection';
import { SalesOrderDetailsSection } from './SalesOrderDetailsSection';
import { SalesOrderItemsSection } from './SalesOrderItemsSection';
import { SalesOrderFooterSection } from './SalesOrderFooterSection';
import { Modal } from '../../components/common/Modal';
import { CustomerForm } from '../selling/CustomerForm';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useLanguage } from '../../features/language/useLanguage';

interface Props {
  initialData?: SalesOrderFormData;
  isOpen?: boolean;
  onSubmit: (data: SalesOrderFormData) => Promise<void>;
  onCancel: () => void;
  readOnly?: boolean;
}

export const CreateSalesOrderForm: React.FC<Props> = ({ initialData, isOpen = true, onSubmit, onCancel, readOnly = false }) => {
  const { t } = useLanguage();
  // Derive edit mode from whether initial data was provided (edit/mark-as flow)
  // vs a blank create modal. Used for the DOM marker consumed by the guide overlay.
  const isEditing = !!initialData;
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    errors,
    isSubmitting,
    fields,
    append,
    remove,
    inventoryItems,
    customers,
    customerOptions,
    loading,
    isDataLoading,
    isSuccess,
    isError,
    isCancelWarningOpen,
    setIsCancelWarningOpen,
    pendingSubmitData,
    areFieldsDisabled,
    isStatusDisabled,
    isPaymentStatusDisabled,
    restrictStatusOptions,
    subtotalAmount,
    discountType,
    discountValue,
    discountAmount,
    shippingAmount,
    totalAmount,
    balanceDue,
    watchedItems,
    currencySymbol,
    company,
    user,
    processSubmit,
    handleFormSubmit,
    isCreateCustomerModalOpen,
    createCustomerInitialName,
    openCreateCustomerModal,
    closeCreateCustomerModal,
    handleCreateCustomer,
    quantityLimitErrors,
    hasQuantityLimitError,
    hasInvalidUnitPriceError,
    isShippingAddressMissing,
    displayUnit,
    setDisplayUnit,
    ownReservedByItem,
    gbLotsByItem
  } = useSalesOrderForm({ initialData, isOpen, onSubmit, onCancel, readOnly });

  const createCustomerInitialData: Customer | undefined = createCustomerInitialName
    ? {
        id: '',
        name: createCustomerInitialName,
        code: '',
        email: '',
        phone: '',
        billingAddress: '',
        shippingAddress: '',
        messageOnInvoice: '',
        internalNotes: '',
        createdAt: '',
        updatedAt: ''
      }
    : undefined;

  if (isDataLoading) {
    return (
      <div data-test-id="so-form-loading">
        <LoadingSpinner text={initialData ? 'Loading sales order details...' : 'Loading sales order form...'} />
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Marker for the onboarding guide overlay to distinguish create vs edit/mark-as modal.
            sr-only (not `hidden`): the overlay detects it via isElementVisible, which rejects
            display:none — an sr-only span stays laid out (1px) yet invisible, so it's detectable. */}
        <span
          data-test-id={isEditing ? 'sales-order-edit-form' : 'sales-order-create-form'}
          className="sr-only"
          aria-hidden
        />
        <SalesOrderHeaderSection
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          customers={customers}
          customerOptions={customerOptions}
          areFieldsDisabled={areFieldsDisabled}
          balanceDue={balanceDue}
          currencySymbol={currencySymbol}
          initialData={initialData}
          onCreateCustomer={(name) => openCreateCustomerModal(name)}
        />

        <hr className="border-t-1 border-gray-400" />

        <SalesOrderDetailsSection
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          areFieldsDisabled={areFieldsDisabled}
          isStatusDisabled={isStatusDisabled}
          isPaymentStatusDisabled={isPaymentStatusDisabled}
          restrictStatusOptions={restrictStatusOptions}
        />

        <hr className="border-t-2 border-gray-300" />

        <SalesOrderItemsSection
          fields={fields}
          watchedItems={watchedItems}
          inventoryItems={inventoryItems}
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          append={append}
          remove={remove}
          currencySymbol={currencySymbol}
          areFieldsDisabled={areFieldsDisabled}
          subtotalAmount={subtotalAmount}
          discountType={discountType}
          discountValue={discountValue}
          discountAmount={discountAmount}
          shippingAmount={shippingAmount}
          totalAmount={totalAmount}
          balanceDue={balanceDue}
          quantityLimitErrors={quantityLimitErrors}
          displayUnit={displayUnit}
          setDisplayUnit={setDisplayUnit}
          ownReservedByItem={ownReservedByItem}
          gbLotsByItem={gbLotsByItem}
        />

        <SalesOrderFooterSection
          register={register}
          watch={watch}
          initialData={initialData}
          totalAmount={totalAmount}
          currencySymbol={currencySymbol}
          company={company}
          user={user}
          customers={customers}
          loading={loading}
          isSubmitting={isSubmitting}
          isSuccess={isSuccess}
          isError={isError}
          onCancel={onCancel}
          isCancelWarningOpen={isCancelWarningOpen}
          setIsCancelWarningOpen={setIsCancelWarningOpen}
          pendingSubmitData={pendingSubmitData}
          processSubmit={processSubmit}
          isSubmitDisabled={hasQuantityLimitError || hasInvalidUnitPriceError || isShippingAddressMissing}
          readOnly={readOnly}
        />
      </form>

      <Modal
        isOpen={isCreateCustomerModalOpen}
        onClose={closeCreateCustomerModal}
        title={t('selling.customerList.actions.add')}
        maxWidth="2xl"
      >
        <CustomerForm
          initialData={createCustomerInitialData}
          onSubmit={handleCreateCustomer}
          onCancel={closeCreateCustomerModal}
          existingCodes={customers.map(c => c.code || '')}
        />
      </Modal>
    </>
  );
};
